import { AsyncPipe, LowerCasePipe, NgForOf, NgIf, NgOptimizedImage, NgSwitch, NgSwitchCase } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TuiButtonModule, TuiDialogContext, TuiRootModule, TuiWrapperModule } from '@taiga-ui/core';
import {
    TuiCheckboxBlockModule,
    TuiCheckboxLabeledModule,
    TuiCheckboxModule,
    TuiRadioBlockModule,
    TuiRadioLabeledModule,
    TuiRadioListModule,
    TuiTextAreaModule
} from '@taiga-ui/kit';
import {
    ComboItem,
    FloorPipeModule,
    FoodOptionItem,
    FoodOptionSelectType,
    OrderItem
} from '@us/web/shared/data-access';
import { NumberGroupComponent } from '@us/web/shared/ui';
import { FormatCurrencyPipe, selectShop, selectTurnOnTheStoreChainLinkFeature } from '@us/web/shop/data-access';
import { BehaviorSubject, combineLatest, combineLatestWith, filter, map, startWith } from 'rxjs';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { ProductStore } from '@us/web/categories/data-access';
import { TuiIdentityMatcher, TuiLetModule } from '@taiga-ui/cdk';
import { ProductPhotoComponent } from '../product-photo/product-photo.component';
import { TuiSheetModule } from '@taiga-ui/addon-mobile';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';

@UntilDestroy()
@Component({
    selector: 'us-product-item-modal',
    templateUrl: './product-item-modal.component.html',
    styleUrls: ['./product-item-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgOptimizedImage,
        TuiRadioLabeledModule,
        FormsModule,
        TuiTextAreaModule,
        TranslocoModule,
        FloorPipeModule,
        FormatCurrencyPipe,
        NumberGroupComponent,
        TuiButtonModule,
        TuiWrapperModule,
        TuiRootModule,
        NgIf,
        LowerCasePipe,
        NgForOf,
        NgSwitch,
        NgSwitchCase,
        TuiCheckboxModule,
        AsyncPipe,
        TuiLetModule,
        ReactiveFormsModule,
        TuiRadioListModule,
        ProductPhotoComponent,
        TuiCheckboxLabeledModule,
        TuiCheckboxBlockModule,
        TuiSheetModule,
        LetDirective,
        TuiRadioBlockModule
    ],
    providers: [ProductStore],
    standalone: true
})
export class ProductItemModalComponent implements OnInit {
    protected readonly productStore = inject(ProductStore);
    private readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<
        void,
        {
            productId: number;
            selectedValue: OrderItem | null;
        }
    >;

    private readonly store = inject(Store);

    readonly selectedValue = this.context.data.selectedValue;

    readonly FoodOptionSelectType = FoodOptionSelectType;
    isCloseShop: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    readonly disabledAddToCart$ = combineLatest([
        this.productStore.quantity$,
        this.productStore.productAvailable$
    ]).pipe(map(([quantity, productAvailable]) => quantity <= 0 || !productAvailable));

    readonly optionItemIdentityMatcher: TuiIdentityMatcher<FoodOptionItem> = (t, u) => t.id === u.id;
    readonly comboItemIdentityMatcher: TuiIdentityMatcher<ComboItem> = (t, u) => t.id === u.id;

    ngOnInit(): void {
        this.productStore.patchState({
            productId: this.context.data.productId,
            selectedValue: this.context.data.selectedValue
        });

        this.store.select(selectTurnOnTheStoreChainLinkFeature).pipe(
            startWith(false),
            filter(turnOnTheStoreChainLinkFeature => !turnOnTheStoreChainLinkFeature),
            combineLatestWith(this.store.select(selectShop)),
            map(([, shop]) => {
              const day = new Date().getDay();
              const time = new Date().toLocaleTimeString('en-GB', { hour: 'numeric', minute: 'numeric' });
              const found = shop.open_hours.find(e => e.day === day)?.periods.find(period => period.from < time && period.to > time);
              return !found;
            })
        ).subscribe(isClosed => {
            this.isCloseShop.next(isClosed);
        });
    }

    addToCart() {
        this.productStore.addToCart({});
        this.context.completeWith();
    }
}
